import { createContext, useContext } from 'react';

/**
 * What the app decided about one permission on one record.
 *
 * **A discriminated union with `reason` REQUIRED on the contextual variant, and that is the whole
 * design.** §9's second hard constraint says the reason is required, not optional — "You do not
 * have permission" is not a reason. Written as an optional field, that rule survives exactly as
 * long as everyone remembers it; written as a variant with no optional reason, a denial without one
 * does not type-check. FS-3 AC-19 asks for precisely that: a missing reason is an error, not a silent
 * omission.
 *
 * The two denials are different states rather than a boolean plus a flag because §9 renders them
 * differently and for different reasons — see `PermissionGuard`.
 */
export type PermissionDecision =
    | { readonly kind: 'allowed' }
    /**
     * The role could do this, but not to this record right now — wrong stage, not the owner, quote
     * already issued. Rendered disabled WITH the reason, because the state can change.
     */
    | { readonly kind: 'denied-contextual'; readonly reason: string }
    /**
     * The role can never do this anywhere — principally Executive / Management, read-only by
     * definition. Rendered as nothing at all: a control that stays grey forever is daily noise.
     */
    | { readonly kind: 'denied-permanent' };

/**
 * The seam FS-6 fills.
 *
 * **It takes a PERMISSION name, never a role** — AD #26, which this signature exists to enforce.
 * A guard that asked "is this user an Administrator?" would silently convert every future
 * Administrator into a below-floor-pricing approver, and nothing at the point of granting the admin
 * role would say so. Naming the permission makes the grant visible in the role's permission set and
 * makes splitting the two later a configuration change instead of a code change.
 *
 * `resource` identifies the record when the answer depends on one (AD #15's `(role, resource,
 * permission-level)` model); a permission that is the same everywhere passes nothing.
 */
export type PermissionResolver = (permission: string, resource?: string) => PermissionDecision;

const ALLOWED: PermissionDecision = { kind: 'allowed' };

/**
 * The stub this slice ships: everything is allowed.
 *
 * Permissive rather than restrictive on purpose. FS-6 has not been built, so no real decision
 * exists yet — and a default that HID things would make every screen built between now and then
 * render wrongly while looking deliberate. Allowing everything makes the absence of the driver
 * obvious the moment the first real permission is wired, rather than three screens later.
 */
export const allowAllPermissions: PermissionResolver = () => ALLOWED;

export const PermissionContext = createContext<PermissionResolver>(allowAllPermissions);

/**
 * The decision for one permission, from the nearest provider — or `allowed` with none.
 *
 * Exposed alongside `PermissionGuard` because not every permission question is about wrapping a
 * control: a screen may need to know before it builds a menu, or to choose between two layouts.
 * Both routes read the same resolver, so they cannot disagree.
 */
export function usePermission(permission: string, resource?: string): PermissionDecision {
    return useContext(PermissionContext)(permission, resource);
}
